<!--
  DefaultsPricing -- Distributor App Account Defaults: Pricing card.

  Card: Distributor Onboarding & Account (https://trello.com/c/QV2U4KhF)
  Pebble: Defaults page IA -- collapse pricing/branding sub-pages into
  one page (each setting becomes a stacked card on AccountDefaults).

  Functional model (locked by Jake 2026-06-04):
    * The default base margin is set ONCE per distributor.
    * NULL = "no distributor default" (the resolver falls back to the
      hardcoded 0.40 / 40% constant in code).
    * Per-store override lives in the store editor (StoreBaseMargin
      sub-panel). Per-size margin is a separate future surface on the
      product editor and is OUT of scope here.

  IA (2026-06-24): this is no longer a standalone /defaults/pricing
  route. It renders as a self-contained card hosted by AccountDefaults
  (the single /distributor/account/defaults page). The page-level chrome
  (v-container / md-header) was dropped; the card title + section
  wrapper live here. Write authorization is gated by the host page via
  the ``editable`` prop (false => the Save button is disabled and a
  read-only note shows). The backend PATCH endpoint independently
  enforces distributor.pricing.defaults (Owner-only) as defense in depth.

  Endpoint:
    * GET  /api/distributors/<id>/defaults
        -> {"default_base_margin": <float or null>}
    * PATCH /api/distributors/<id>/defaults
        body {"default_base_margin": <float or null>}
        gated on distributor.pricing.defaults (Owner-only)
-->
<template>
    <section
        class="defaults-pricing-card"
        data-testid="dist-account-defaults-pricing-page"
    >
        <h2 class="defaults-pricing-card__title">Default base margin</h2>

        <p class="defaults-pricing__lede">
            The default base margin applies to new stores you create.
            Individual stores can override it from the store editor's
            Pricing &amp; Checkout section. Setting this back to blank
            uses ThreadFlow's platform fallback of 40%.
        </p>

        <v-progress-circular
            v-if="loading"
            :indeterminate="true"
            color="primary"
            size="32"
            width="4"
            class="mt-4"
            data-testid="defaults-pricing-loading"
        />

        <div v-else class="defaults-pricing__form">
            <label
                class="defaults-pricing__label"
                for="defaults-pricing-margin-input"
            >
                Default base margin
            </label>
            <div class="defaults-pricing__input-row">
                <input
                    id="defaults-pricing-margin-input"
                    v-model="marginPercentDraft"
                    type="number"
                    inputmode="decimal"
                    step="1"
                    min="0"
                    max="100"
                    placeholder="40"
                    :disabled="!editable"
                    class="defaults-pricing__input"
                    data-testid="defaults-pricing-margin-input"
                    @input="onInput"
                />
                <span class="defaults-pricing__suffix">%</span>
            </div>
            <p
                v-if="errorMsg"
                class="defaults-pricing__error"
                data-testid="defaults-pricing-error"
            >
                {{ errorMsg }}
            </p>
            <p
                v-else
                class="defaults-pricing__hint"
                data-testid="defaults-pricing-hint"
            >
                {{ hint }}
            </p>

            <p
                v-if="!editable"
                class="defaults-pricing__readonly"
                data-testid="defaults-pricing-readonly"
            >
                Only the distributor Owner can change this default.
            </p>

            <div class="defaults-pricing__actions">
                <button
                    type="button"
                    class="tf-btn tf-btn--primary"
                    :disabled="!canSave"
                    data-testid="defaults-pricing-save"
                    @click="save"
                >
                    {{ saving ? "Saving..." : "Save" }}
                </button>
                <button
                    v-if="hasStoredValue"
                    type="button"
                    class="tf-btn tf-btn--ghost"
                    :disabled="saving || !editable"
                    data-testid="defaults-pricing-clear"
                    @click="clear"
                >
                    Reset to fallback
                </button>
            </div>
        </div>
    </section>
</template>

<script>
import { computed, ref, onMounted } from "vue";
import axios from "@/axios";
import { useAccountStore } from "@/stores/account";
import { notifyPricingDefaultsChanged } from "@/stores/sidebarRefresh";

// The hardcoded last-resort percent that applies when neither the
// SA-global nor the distributor default is set. Tracks the backend
// constant (_HARDCODED_FALLBACK_MARGIN in
// api/src/endpoints/distributor_defaults.py).
const FALLBACK_PERCENT = 40;

export default {
    name: "DefaultsPricing",

    props: {
        // The distributor id can be passed explicitly for tests + admin
        // surfaces; in the live app it falls back to the Pinia
        // useAccountStore() current-tenant id.
        distributorId: {
            type: [Number, String],
            default: null,
        },
        // Write authorization, supplied by the host page (AccountDefaults)
        // from usePermissions().can('distributor.pricing.defaults'). When
        // false the Save / Reset affordances are disabled and a read-only
        // note shows. The backend PATCH still enforces the cap server-side
        // (defense in depth); this prop is the UX layer of that gate.
        editable: {
            type: Boolean,
            default: true,
        },
    },

    setup(props) {
        let resolvedId = props.distributorId;
        if (resolvedId === null || resolvedId === "") {
            try {
                const account = useAccountStore();
                resolvedId =
                    account.distributor && account.distributor.id
                        ? account.distributor.id
                        : null;
            } catch (e) {
                resolvedId = null;
            }
        }
        const distributorId = ref(resolvedId);

        // Stored value is the fraction in [0, 1] from the backend, or
        // null when the distributor has not set a default.
        const storedFraction = ref(null);
        // The SA-global default base margin -- pulled from the same
        // GET response. Null when no SA-global is set, in which case
        // the resolver falls through to the hardcoded FALLBACK_PERCENT.
        // Used by the inheritance hint when the distributor's own
        // default is null.
        const saGlobalFraction = ref(null);
        // Draft is the integer-ish percent the user types (e.g. "40").
        const marginPercentDraft = ref("");
        const loading = ref(true);
        const saving = ref(false);
        const errorMsg = ref("");

        const hasStoredValue = computed(() => storedFraction.value !== null);

        const hint = computed(() => {
            if (storedFraction.value === null) {
                // 4-level hierarchy: distributor null -> SA-global (if
                // set) -> hardcoded fallback. The hint must reflect
                // which level is actually being inherited.
                if (saGlobalFraction.value !== null) {
                    const pct = Math.round(saGlobalFraction.value * 100);
                    return `No distributor default set. New stores inherit the platform default (${pct}%).`;
                }
                return `No default set. New stores use the platform fallback (${FALLBACK_PERCENT}%).`;
            }
            const pct = Math.round(storedFraction.value * 100);
            return `Current default: ${pct}%. New stores inherit this value.`;
        });

        const canSave = computed(() => {
            if (!props.editable) return false;
            if (saving.value) return false;
            if (errorMsg.value) return false;
            // Allow saving an empty input to clear the override.
            if (marginPercentDraft.value === "") {
                return storedFraction.value !== null;
            }
            const n = Number(marginPercentDraft.value);
            if (!Number.isFinite(n)) return false;
            if (n < 0 || n > 100) return false;
            return true;
        });

        const url = computed(() => {
            if (!distributorId.value) return null;
            return `/api/distributors/${distributorId.value}/defaults`;
        });

        async function load() {
            if (!url.value) {
                loading.value = false;
                return;
            }
            loading.value = true;
            errorMsg.value = "";
            try {
                const resp = await axios.get(url.value);
                const value = resp?.data?.default_base_margin ?? null;
                storedFraction.value = value;
                // The GET response carries the SA-global value alongside
                // the distributor's own value so the inheritance hint
                // can render the right chain level without a second
                // call. Older responses (pre-route_fork_020) lack the
                // key; default to null in that case so the hint falls
                // back to the hardcoded-fallback voice.
                saGlobalFraction.value =
                    resp?.data?.sa_global_default_base_margin ?? null;
                marginPercentDraft.value =
                    value === null ? "" : String(Math.round(value * 100));
            } catch (e) {
                errorMsg.value = "Could not load distributor defaults.";
            } finally {
                loading.value = false;
            }
        }

        function onInput() {
            errorMsg.value = "";
            if (marginPercentDraft.value === "") return;
            const n = Number(marginPercentDraft.value);
            if (!Number.isFinite(n)) {
                errorMsg.value = "Enter a number between 0 and 100.";
                return;
            }
            if (n < 0 || n > 100) {
                errorMsg.value = "Margin must be between 0 and 100.";
            }
        }

        async function save() {
            // Defense in depth at the function boundary: the Save/Reset
            // buttons are :disabled when !editable, but guard here too so
            // a programmatic call can't fire a write the caller isn't
            // authorized for. The backend independently enforces the cap.
            if (!props.editable) return;
            if (!url.value) return;
            saving.value = true;
            errorMsg.value = "";
            const fraction =
                marginPercentDraft.value === ""
                    ? null
                    : Number(marginPercentDraft.value) / 100;
            try {
                const resp = await axios.patch(url.value, {
                    default_base_margin: fraction,
                });
                const next = resp?.data?.default_base_margin ?? null;
                storedFraction.value = next;
                marginPercentDraft.value =
                    next === null ? "" : String(Math.round(next * 100));
                // The default-margin sub-text on the distributor sidebar's
                // Pricing row reads this value; announce the change so that
                // row refetches. Success-only -- the catch path below leaves
                // the counter untouched. clear() delegates here, so Reset to
                // fallback inherits this emit. https://trello.com/c/g9K52FsA
                notifyPricingDefaultsChanged();
            } catch (e) {
                errorMsg.value = "Could not save default margin.";
            } finally {
                saving.value = false;
            }
        }

        async function clear() {
            if (!props.editable) return;
            marginPercentDraft.value = "";
            await save();
        }

        onMounted(load);

        return {
            distributorId,
            loading,
            saving,
            errorMsg,
            hint,
            marginPercentDraft,
            hasStoredValue,
            canSave,
            onInput,
            save,
            clear,
        };
    },
};
</script>

<style scoped>
.defaults-pricing-card__title {
    font-size: var(--tf-size-h2, 1.25rem);
    font-weight: 600;
    margin-bottom: var(--tf-sp-2, 8px);
    color: var(--tf-text, #1f2937);
}
.defaults-pricing__lede {
    margin-top: var(--tf-sp-2, 8px);
    color: var(--tf-text-muted, #4b5563);
    max-width: 640px;
    line-height: 1.5;
}
.defaults-pricing__readonly {
    margin-top: var(--tf-sp-3, 12px);
    color: var(--tf-text-muted, #4b5563);
    font-size: 0.875rem;
    font-style: italic;
}
.defaults-pricing__form {
    margin-top: var(--tf-sp-5, 24px);
    max-width: 320px;
}
.defaults-pricing__label {
    display: block;
    font-weight: 600;
    margin-bottom: var(--tf-sp-2, 8px);
    color: var(--tf-text, #1f2937);
}
.defaults-pricing__input-row {
    display: flex;
    align-items: center;
    gap: var(--tf-sp-2, 8px);
}
.defaults-pricing__input {
    flex: 1;
    padding: var(--tf-sp-2, 8px) var(--tf-sp-3, 12px);
    border: 1px solid var(--tf-border, #d1d5db);
    border-radius: var(--tf-radius-sm, 6px);
    font-size: var(--tf-size-body, 1rem);
    max-width: 120px;
}
.defaults-pricing__suffix {
    color: var(--tf-text-muted, #4b5563);
    font-size: var(--tf-size-body, 1rem);
}
.defaults-pricing__hint {
    margin-top: var(--tf-sp-2, 8px);
    color: var(--tf-text-muted, #4b5563);
    font-size: 0.875rem;
}
.defaults-pricing__error {
    margin-top: var(--tf-sp-2, 8px);
    color: var(--tf-error, #b91c1c);
    font-size: 0.875rem;
}
.defaults-pricing__actions {
    margin-top: var(--tf-sp-4, 16px);
    display: flex;
    gap: var(--tf-sp-2, 8px);
}
.tf-btn {
    border: 1px solid transparent;
    border-radius: var(--tf-radius-sm, 6px);
    padding: var(--tf-sp-2, 8px) var(--tf-sp-5, 20px);
    font-size: var(--tf-size-body, 1rem);
    cursor: pointer;
}
.tf-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}
.tf-btn--primary {
    background: var(--tf-primary, #1f2937);
    color: var(--tf-on-primary, var(--tf-bg));
}
.tf-btn--ghost {
    background: transparent;
    color: var(--tf-text, #1f2937);
    border-color: var(--tf-border, #d1d5db);
}
</style>
